/**
 * variant.ts — runtime-swappable model variant specification.
 *
 * The mob app hosts THREE model variants behind a common surface:
 *
 *   - `mob`        (baseline) — 32 leap actions, 11 features, length-8 gate.
 *   - `mob_knight` (knight)   — 40 actions (32 leaps + 8 knight L-shapes),
 *                                14 features (11 base + 3 enriched), same gate.
 *   - `mob_global` (global)   — 40 actions (32 leaps + 8 queen jumps),
 *                                11 features, same gate. Queen jumps go to
 *                                the argmax-density cell in each of the 8
 *                                directions; the Q-net learns when each
 *                                direction pays off.
 *
 * The variant is selected at runtime via the `?model=` URL param or the
 * sidebar toggle. Because the variants have different action and feature
 * dimensionalities, the constants `N_ACTIONS` / `N_FEATURES` that used to
 * live as module-level compile-time values in `environment.ts` /
 * `features.ts` cannot be shared. Instead, every dimensioned consumer
 * (`Agent`, `LinearQ`, `Replay`, `BypassLabelBuffer`) takes a `VariantSpec`
 * and sizes its buffers off `spec.nActions` / `spec.nFeatures`.
 *
 * The leap action space is identical in all variants: 8 directions × 4
 * lengths {1,2,4,8} = 32 leap actions at indices 0..31. Knight actions
 * (when enabled) are APPENDED at indices 32..39, and queen actions (when
 * enabled) are also appended at indices 32..39 — only one of `useKnight`
 * / `useQueen` is true per variant. Both live after the length-8 group.
 * This keeps the length-8 bypass gate's masking logic (which masks indices
 * `nLeapActions - 8 .. nLeapActions - 1`) correct in all variants: knight
 * and queen actions are never masked by the gate, which is the intended
 * behaviour (they are ALTERNATIVES to bypassing length-8).
 *
 * Knight move geometry: each (dr, dc) in KNIGHT_MOVES has Chebyshev
 * distance 2 (one axis ±2, the other ±1). These 8 cells are NOT reachable
 * by any straight leap — they are the only cells within 2-step reach that
 * no leap action can hit. See `_proto_knight_v2.py` for the validated
 * prototype: knight + 3 enriched features recovers ~13% of gold on
 * clustered worlds that the baseline planner leaves on the table.
 *
 * Queen move geometry: action `nLeapActions + i` jumps toward
 * DIRECTIONS[i] — the target is the argmax-density cell along that
 * direction (Chebyshev-uncapped), walked cell-by-cell with the standard
 * reward function. See `_proto_global_v3.py` for the validated prototype:
 * queen + density map recovers ~15% of gold on random worlds that the
 * baseline planner leaves on the table due to step-cap.
 */

/** Runtime model variant identifier (also the `?model=` URL param value). */
export type ModelKind = 'mob' | 'mob_knight' | 'mob_global';

/** World generator selector for the per-episode rotation. */
export type WorldType = 'auto' | 'cluster' | 'spiral' | 'random' | 'empty';

/**
 * Per-variant dimensionality + capability flags. Passed into `Agent`,
 * `LinearQ`, `Replay`, and `BypassLabelBuffer` constructors so they size
 * their internal buffers correctly.
 */
export interface VariantSpec {
  /** URL param value + UI key. */
  kind: ModelKind;
  /** Total action count = nLeapActions + (useKnight ? N_KNIGHT : 0)
   *  + (useQueen ? N_QUEEN : 0). */
  nActions: number;
  /** Per-action feature count. 11 baseline; 14 with knight enriched. */
  nFeatures: number;
  /** Leap-action count (always 32 = 8 dirs × 4 lengths). The length-8
   *  group occupies indices `[nLeapActions - 8, nLeapActions)`. */
  nLeapActions: number;
  /** When true, knight actions are appended after the leap group and the
   *  3 enriched features are written. When false, neither exists. */
  useKnight: boolean;
  /** When true, 8 queen actions are appended after the leap group. Each
   *  queen action jumps toward one of 8 directions; the target is the
   *  argmax-density cell along that direction. Mutually exclusive with
   *  useKnight (one extended action set per variant). */
  useQueen: boolean;
}

/** Baseline variant: 32 leaps × 11 features. Length-8 gate applies. */
export const MOB_BASELINE: VariantSpec = {
  kind: 'mob',
  nActions: 32,
  nFeatures: 11,
  nLeapActions: 32,
  useKnight: false,
  useQueen: false,
};

/** Knight variant: 40 actions (32 leaps + 8 knight) × 14 features. */
export const MOB_KNIGHT: VariantSpec = {
  kind: 'mob_knight',
  nActions: 40,
  nFeatures: 14,
  nLeapActions: 32,
  useKnight: true,
  useQueen: false,
};

/** Global variant: 40 actions (32 leaps + 8 queen jumps) × 11 features.
 *  Queen jumps go to argmax-density cells; the density map is computed
 *  from the existing belief (no new sensor). The Q-net learns when each
 *  direction pays off — no separate gate needed. */
export const MOB_GLOBAL: VariantSpec = {
  kind: 'mob_global',
  nActions: 40,
  nFeatures: 11,
  nLeapActions: 32,
  useKnight: false,
  useQueen: true,
};

/** All recognised variant specs, keyed by `kind`. */
export const VARIANTS: Readonly<Record<ModelKind, VariantSpec>> = {
  mob: MOB_BASELINE,
  mob_knight: MOB_KNIGHT,
  mob_global: MOB_GLOBAL,
};

/**
 * The 8 knight L-shape moves (dr, dc), indexed 0..7. Each lands on a cell
 * with Chebyshev distance 2 from the origin — unreachable by any straight
 * leap (leaps reach cells at axis-aligned or diagonal distances only).
 *
 * Layout matches the Python prototype `_proto_knight_v2.py`:
 *   indices 0..3 — long leg is N/S (|dr| == 2)
 *   indices 4..7 — long leg is E/W (|dc| == 2)
 */
export const KNIGHT_MOVES: ReadonlyArray<readonly [number, number]> = [
  [-2, -1], [-2, 1], [2, -1], [2, 1],   // long leg vertical (dr = ±2)
  [-1, -2], [-1, 2], [1, -2], [1, 2],   // long leg horizontal (dc = ±2)
];

/** Number of knight actions (always 8 — fixed by KNIGHT_MOVES). */
export const N_KNIGHT = 8;

/** Number of queen actions (always 8 — one per direction in DIRECTIONS). */
export const N_QUEEN = 8;

/**
 * Return both candidate L-paths for a knight move from (r, c). Each path
 * is a 3-cell walk; both paths end at the same destination cell. The env
 * walks whichever path is clear (path A preferred if both are clear).
 *
 * For a move with |dr| == 2 (long leg vertical):
 *   path A: (r,c) → (r±1, c)     → (r±2, c)     → (r±2, c±1)
 *   path B: (r,c) → (r, c±1)     → (r±1, c±1)   → (r±2, c±1)
 *
 * For a move with |dc| == 2 (long leg horizontal): the symmetric pair.
 *
 * Both paths are orthogonal — no diagonal corner-cutting rule needed.
 */
export function knightPaths(
  r: number,
  c: number,
  moveIdx: number,
): [readonly [number, number], readonly [number, number], readonly [number, number]][] {
  const [dr, dc] = KNIGHT_MOVES[moveIdx];
  const sr = dr > 0 ? 1 : -1;  // sign of the long vertical leg
  const sc = dc > 0 ? 1 : -1;  // sign of the long horizontal leg
  if (Math.abs(dr) === 2) {
    const dest: readonly [number, number] = [r + dr, c + dc];
    const pathA: [readonly [number, number], readonly [number, number], readonly [number, number]] = [
      [r + sr, c],
      [r + 2 * sr, c],
      dest,
    ];
    const pathB: [readonly [number, number], readonly [number, number], readonly [number, number]] = [
      [r, c + dc],
      [r + sr, c + dc],
      dest,
    ];
    return [pathA, pathB];
  }
  // |dc| === 2 — long leg horizontal
  const dest: readonly [number, number] = [r + dr, c + dc];
  const pathA: [readonly [number, number], readonly [number, number], readonly [number, number]] = [
    [r, c + sc],
    [r, c + 2 * sc],
    dest,
  ];
  const pathB: [readonly [number, number], readonly [number, number], readonly [number, number]] = [
    [r + dr, c],
    [r + dr, c + sc],
    dest,
  ];
  return [pathA, pathB];
}

/** True iff action index `a` is a knight action in spec `spec`. */
export function isKnightAction(a: number, spec: VariantSpec): boolean {
  return spec.useKnight && a >= spec.nLeapActions && a < spec.nLeapActions + N_KNIGHT;
}

/** Convert a knight action id to its index into KNIGHT_MOVES (0..7). */
export function knightMoveIdx(a: number, spec: VariantSpec): number {
  return a - spec.nLeapActions;
}

/** True iff action index `a` is a queen action in spec `spec`. */
export function isQueenAction(a: number, spec: VariantSpec): boolean {
  return spec.useQueen && a >= spec.nLeapActions && a < spec.nLeapActions + N_QUEEN;
}

/** Convert a queen action id to its direction index 0..7 (into DIRECTIONS). */
export function queenDirIdx(a: number, spec: VariantSpec): number {
  return a - spec.nLeapActions;
}

/** Look up the spec for a model kind. Defaults to baseline for unknown. */
export function specFor(kind: ModelKind): VariantSpec {
  return VARIANTS[kind] ?? MOB_BASELINE;
}

/** Parse the `?model=` URL param. Defaults to 'mob' for unknown/missing. */
export function parseModelParam(s: string | null): ModelKind {
  if (s === 'mob_knight') return 'mob_knight';
  if (s === 'mob_global') return 'mob_global';
  return 'mob';
}

/** Parse the `?world=` URL param. Defaults to 'auto' for unknown/missing. */
export function parseWorldParam(s: string | null): WorldType {
  if (s === 'cluster' || s === 'spiral' || s === 'random' || s === 'empty') return s;
  return 'auto';
}
